import {
  CobaltTable,
  CobaltTableBody,
  CobaltTableCell,
  CobaltTableContainer,
  CobaltTableHead,
  CobaltTableRow,
} from '@cae/cobalt-react/components/table';
import { useHasPermission } from '@contexts/permissions-provider/hooks/use-has-permission/useHasPermission';
import { CreditMemoDownloadCell } from '@features/invoices/components/detail-panel/credit-memo-download-cell/CreditMemoDownloadCell';
import { formatAmount } from '@features/invoices/utils/invoices.utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';

import type { CreditMemosTableProps } from './CreditMemosTable.types';

import styles from './CreditMemosTable.module.css';
import { TABLE_COLUMNS } from './CreditMemosTable.utils';

const CreditMemosTable = React.memo(
  ({ creditMemos }: Readonly<CreditMemosTableProps>): React.ReactElement => {
    const canDownload = useHasPermission('Invoices_Open_Org');
    const sorted = React.useMemo(
      () =>
        [...creditMemos].sort(
          (a, b) =>
            new Date(b.appliedOn || 0).getTime() - new Date(a.appliedOn || 0).getTime()
        ),
      [creditMemos]
    );

    return (
      <CobaltTableContainer>
        <CobaltTable size="medium" data-testid="invoice-credits-table">
          <CobaltTableHead>
            <CobaltTableRow>
              {TABLE_COLUMNS.map(column => (
                <CobaltTableCell
                  key={column.id}
                  header
                  aria-label={column.ariaLabel}
                  style={{ minWidth: column.width }}
                >
                  {column.label || null}
                </CobaltTableCell>
              ))}
            </CobaltTableRow>
          </CobaltTableHead>
          <CobaltTableBody>
            {sorted.map((credit, index) => (
              <CobaltTableRow
                key={credit.creditMemoNumber ?? credit.id ?? index}
                data-testid="invoice-credit-row"
                className={styles.tableRow}
              >
                <CobaltTableCell>
                  <DataValue value={credit.creditMemoNumber} />
                </CobaltTableCell>
                <CobaltTableCell>
                  <DataValue value={formatDateShort(credit.appliedOn)} />
                </CobaltTableCell>
                <CobaltTableCell>
                  <DataValue value={formatAmount(credit.amount, credit.currency)} />
                </CobaltTableCell>
                <CobaltTableCell className={styles.downloadCell}>
                  {credit.isDownloadAvailable && canDownload && credit.id != null && credit.creditMemoNumber != null && (
                    <CreditMemoDownloadCell
                      creditMemoId={credit.id}
                      creditMemoNumber={credit.creditMemoNumber}
                    />
                  )}
                </CobaltTableCell>
              </CobaltTableRow>
            ))}
          </CobaltTableBody>
        </CobaltTable>
      </CobaltTableContainer>
    );
  }
);

CreditMemosTable.displayName = 'CreditMemosTable';
export { CreditMemosTable };
